<template>
  <v-col
    ref="gridRow"
    v-resize="onResize"
    cols="12"
    class="d-flex justify-center"
    style="flex-flow: wrap"
  >
    <template v-for="(slot, i) in hourSlots">
      <div
        v-if="i % itemsPerRow === 0"
        :key="`flex-break-${i}`"
        class="flex-break-column"
      />
      <v-btn
        :key="`slot_${i}`"
        :ref="`slot_btn_${i}`"
        :small="$vuetify.breakpoint.xsOnly"
        class="openinghours-slot-button ma-1 flex-lg-wrap"
        :style="`color: ${textColorForSlot(colorForSlot(slot))} !important`"
        :color="colorForSlot(slot)"
        rounded
        @mousedown.native.stop="slotButtonMouseDown(i, $event)"
        @click="openMenu(i, $event)"
      >
        <v-icon
          v-if="slot && slot.isSpecial"
          class="pr-0"
          color="white"
        >
          mdi-circle-medium
        </v-icon>
        <template v-if="slot && slot.isOpen">
          {{ $localized.hourMinTextFrom24hr(slot.time) }}
        </template>
        <template v-else>
          {{ $localized.hourMinTextFrom24hr(titleForClosedSlot(i)) }}
        </template>
      </v-btn>
    </template>
    <v-menu
      ref="menu"
      bottom
      offset-y
      :close-on-click="true"
      max-height="75%"
    >
      <v-list class="py-0">
        <v-list-item @click="openMenuItemSelected">
          <v-list-item-icon class="mr-3">
            <v-icon :color="colors[1]">
              mdi-circle
            </v-icon>
          </v-list-item-icon>
          <v-list-item-title>{{ $t('label.open') }}</v-list-item-title>
        </v-list-item>
        <v-divider />

        <v-list-item @click="closedMenuItemSelected">
          <v-list-item-icon class="mr-3">
            <v-icon :color="colors[0]">
              mdi-circle
            </v-icon>
          </v-list-item-icon>
          <v-list-item-title>{{ $t('label.closed') }}</v-list-item-title>
        </v-list-item>

        <template v-for="(profile, i) in profiles">
          <v-divider :key="`${i}_separator`" />
          <v-list-item
            :key="`${i}_profile_item`"
            class="mr-5"
            style="width:420px"
            @click="menuProfileSelected(profile.id)"
          >
            <v-list-item-icon class="my-auto mr-3">
              <v-icon :color="colorForProfileId(profile.id)">
                mdi-circle
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content class="text-caption pa-0">
              <v-row class="my-0">
                <!-- minimumPartySize -->
                <v-col
                  v-if="profile.minimumPartySize"
                  cols="12"
                  sm="6"
                  class="py-1"
                >
                  <span class>{{ $t('label.min_party_size') }}: </span>
                  <span
                    class="font-weight-bold"
                  >{{ profile.minimumPartySize || $t('label.option_any') }}</span>
                </v-col>
                <!-- maximumPartySize -->
                <v-col
                  v-if="profile.maximumPartySize"
                  cols="12"
                  sm="6"
                  class="py-1"
                >
                  {{ $t('label.max_party_size') }}:
                  <span
                    class="font-weight-bold"
                  >{{ profile.maximumPartySize || $t('label.option_any') }}</span>
                </v-col>
                <!-- maximumGuests -->
                <v-col
                  v-if="profile.maximumGuests"
                  cols="12"
                  sm="6"
                  class="py-1"
                >
                  {{ $t('label.max_guests_per_time') }}:
                  <span
                    class="font-weight-bold"
                  >{{ profile.maximumGuests || $t('label.option_any') }}</span>
                </v-col>
                <!-- maximumReservations -->
                <v-col
                  v-if="profile.maximumReservations"
                  cols="12"
                  sm="6"
                  class="py-1"
                >
                  {{ $t('label.max_reservations_per_time') }}:
                  <span
                    class="font-weight-bold"
                  >{{ profile.maximumReservations || $t('label.option_any') }}</span>
                </v-col>
                <!-- fixedEndingInMinutes -->
                <v-col
                  v-if="profile.fixedEndingInMinutes"
                  cols="12"
                  sm="6"
                  class="py-1"
                >
                  {{ $t('label.fixed_ending_time') }}:
                  <span
                    class="font-weight-bold"
                  >{{ profile.fixedEndingInMinutes || $t('label.option_any') }}</span>
                </v-col>
              </v-row>
            </v-list-item-content>
          </v-list-item>
        </template>
        <v-divider />
        <v-list-item @click="customMenuItemSelected">
          <v-list-item-title>{{ $t('button.different_settings') }}</v-list-item-title>
        </v-list-item>
        <v-list-item
          v-if="enableReset"
          @click="resetMenuItemSelected"
        >
          <v-list-item-title>{{ $t('button.reset_to_default') }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
    <!-- Custom Profile Dialog -->
    <hour-slots-profile-dialog
      :visible="newProfileDialog"
      @close="newProfileDialog = false"
      @save-result="customProfileSelected"
    />
  </v-col>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import HourSlotsProfileDialog from '@/components/views/settings/HourSlotsProfileDialog.vue';
import HourSlot from '@/model/HourSlot';
import HourSlotProfile, { colorsForProfiles } from '@/model/HourSlotProfile';
import { niceTimeTextFromSlot } from '@/services/time-utils';
import { contrastForColor } from '@/services/color-utils';

export const HourSlotMenuItemType = {
  CLOSED: 0, OPEN: 1, CUSTOM: 2, CUSTOM_NEW: 3, RESET: 4,
};

export default Vue.extend({
  components: { HourSlotsProfileDialog },
  props: {
    hourSlots: { type: Array as PropType<(HourSlot | null)[]>, required: true },
    profiles: { type: Array as PropType<HourSlotProfile[]>, required: true },
    enableReset: { type: Boolean as PropType<boolean>, required: false, default: false },
  },
  data() {
    return {
      gridWidth: 0,
      activeSlotIndex: 0,
      newProfileDialog: false,
      hourSlot: new HourSlot(),
      colors: [
        '#9e9e9e',
        '#3355ff',
        '#E42DD0',
        '#FF3D94',
        '#FF8262',
        '#FFC24E',
        '#F9F871',
        '#88883E',
        '#00A26F',
        '#E90021',
        '#61FBD5',
        '#9D5B00',
        '#312D4A',
        '#1A5A5F',
        '#1F9199',
        '#A92DC8',
        '#6A000F',
        '#D1C7FF',
        '#0011A3',
        '#F37385',
        '#1C1A27',
        '#938BB7',
      ],
    };
  },
  computed: {
    itemsPerRow(): number {
      return this.gridWidth > 750 ? 8 : 4;
    },
    colorsByProfileId(): Map<number, string> {
      return colorsForProfiles(this.profiles, this.colors.slice(2));
    },
  },
  watch: { },
  methods: {
    titleForClosedSlot(slotNr: number): string {
      return niceTimeTextFromSlot(slotNr + 24);
    },
    colorForProfileId(profileId: number): string | undefined {
      return this.colorsByProfileId.get(profileId);
    },
    colorForSlot(slot: HourSlot | null) {
      if (!slot || !slot.isOpen) return 'grey';
      if (slot.profileId) return this.colorForProfileId(slot.profileId);
      return 'blue';
    },
    slotButtonMouseDown(slotNr: number) {
      const btn = (this.$refs[`slot_btn_${slotNr}`] as any)[0] as Vue;
      (this.$refs.menu as any).activator = btn.$el;
    },
    onResize() {
      this.gridWidth = (this.$refs.gridRow as HTMLElement)?.clientWidth || 0;
    },
    openMenu(slotIndex: number) {
      this.activeSlotIndex = slotIndex;
      this.$emit('timeslot-selected', slotIndex);
    },
    openMenuItemSelected() {
      this.$emit('profile-selected', this.activeSlotIndex, HourSlotMenuItemType.OPEN);
    },
    closedMenuItemSelected() {
      this.$emit('profile-selected', this.activeSlotIndex, HourSlotMenuItemType.CLOSED);
    },
    customMenuItemSelected() {
      this.newProfileDialog = true;
    },
    resetMenuItemSelected(type: number) {
      this.$emit('profile-selected', this.activeSlotIndex, HourSlotMenuItemType.RESET);
    },
    customProfileSelected(hourSlot: HourSlot) {
      this.$emit('profile-selected', this.activeSlotIndex, HourSlotMenuItemType.CUSTOM_NEW, hourSlot);
      this.hourSlot.clearParams();
    },
    menuProfileSelected(profileId: number) {
      this.$emit('profile-selected', this.activeSlotIndex, HourSlotMenuItemType.CUSTOM, profileId);
    },
    textColorForSlot(color: string): string {
      return contrastForColor(color);
    },
  },
});
</script>

<style scoped>

  .openinghours-slot-button {
    width: 76px;
  }

</style>
